/**
 * Acelera el salto LoginPortal → tenant WMS:
 * - preconnect/prefetch del origen/document del tenant
 * - same-origin: handoff opaco desde el portal (salta carga SPA de /m/…)
 */
import { persistSameOriginHandoffSession } from './persistPortalHandoffSession';
import { markAuthTransitionMilestone } from '../utils/authTransition';

const OPAQUE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const WMS_API_BASE = '/api/backend';

function ensureHeadLink({ rel, href, asValue }) {
  if (typeof document === 'undefined' || !href) return;
  const existing = document.head.querySelector(`link[rel="${rel}"][href="${href}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (asValue) link.as = asValue;
  document.head.appendChild(link);
}

/**
 * Calienta DNS/TLS y precarga el documento del tenant (HTML shell).
 * @param {string} tenantUrl
 */
export function warmupTenantNavigation(tenantUrl) {
  try {
    const dest = new URL(tenantUrl, window.location.origin);
    ensureHeadLink({ rel: 'dns-prefetch', href: dest.origin });
    ensureHeadLink({ rel: 'preconnect', href: dest.origin });
    ensureHeadLink({ rel: 'prefetch', href: dest.toString(), asValue: 'document' });
    // Misma origen: el index del shell trae los hashes de JS; prefetch ayuda al siguiente nav.
    if (dest.origin === window.location.origin) {
      ensureHeadLink({ rel: 'prefetch', href: `${dest.origin}/`, asValue: 'document' });
    }
  } catch {
    /* ignore */
  }
}

function extractOpaqueId(tenantUrl) {
  try {
    const path = new URL(tenantUrl, window.location.origin).pathname || '';
    const m = path.match(/\/m\/([0-9a-f-]{36})\/?/i);
    return m && OPAQUE_UUID_RE.test(m[1]) ? m[1].toLowerCase() : null;
  } catch {
    return null;
  }
}

import { resolvePortalHandoffPostLoginPath } from '../utils/postLoginRedirect';

/** Parity shell `handoffNeedsProfileSelection` — no entrar a la app con multi-perfil. */
function handoffNeedsProfileSelection(data) {
  if (!data || typeof data !== 'object') return false;
  const root = data.data && typeof data.data === 'object' ? data.data : data;
  if (root.requiere_seleccion_perfil === true || root.requiere_seleccion_perfil === 1) {
    return true;
  }
  if (String(root.modo_acceso || '').toLowerCase() === 'pendiente_seleccion_perfil') {
    return true;
  }
  const total = Number(root.total_perfiles_disponibles);
  if (Number.isFinite(total) && total > 1) return true;
  if (Array.isArray(root.perfiles_disponibles) && root.perfiles_disponibles.length > 1) {
    return true;
  }
  if (root.perfil_propio && root.perfil_prestado) return true;
  return false;
}

/**
 * Si portal y tenant comparten origen, canjea opaque acá (cookies same-site)
 * y navega directo a la app / picker de perfil — sin montar /m/:id.
 *
 * @param {string} tenantUrl
 * @param {{ tigerSim?: boolean }} [opts]
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function trySameOriginOpaqueHandoff(tenantUrl, opts = {}) {
  let dest;
  try {
    dest = new URL(tenantUrl, window.location.origin);
  } catch {
    return { ok: false, reason: 'bad_url' };
  }
  if (dest.origin !== window.location.origin) {
    return { ok: false, reason: 'cross_origin' };
  }

  const opaqueId = extractOpaqueId(dest.toString());
  if (!opaqueId) return { ok: false, reason: 'no_opaque' };

  warmupTenantNavigation(dest.toString());
  markAuthTransitionMilestone('SHELL_HANDOFF');

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${WMS_API_BASE}/auth_api/custom/portal-handoff-opaque`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ opaque_id: opaqueId }),
      signal: controller.signal,
    });
    const text = await response.text();
    let envelope = null;
    try {
      envelope = text ? JSON.parse(text) : null;
    } catch {
      envelope = null;
    }
    if (!response.ok) {
      return {
        ok: false,
        reason: envelope?.description || `handoff_http_${response.status}`,
      };
    }
    const data = envelope?.data && typeof envelope.data === 'object' ? envelope.data : envelope;
    if (!data) return { ok: false, reason: 'empty_handoff' };

    // Crítico: sin esto el shell no tiene tiger_portal_* → logout / cambio de perfil fallan.
    persistSameOriginHandoffSession(data);
    markAuthTransitionMilestone('SESSION_OK');

    const tigerSimSuffix = opts.tigerSim ? '&tigerSim=1' : '';
    // Multi-perfil: siempre picker (aunque BFF auto-resuelva por cookie vieja).
    if (handoffNeedsProfileSelection(data)) {
      window.location.replace(`/login?step=profile${tigerSimSuffix}`);
      return { ok: true };
    }

    let target = resolvePortalHandoffPostLoginPath(data);
    if (opts.tigerSim) {
      const u = new URL(target, window.location.origin);
      u.searchParams.set('tigerSim', '1');
      target = `${u.pathname}${u.search}${u.hash}`;
    }
    window.location.replace(target);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.name === 'AbortError' ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timeout);
  }
}
